import { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { useMutation } from "@apollo/react-hooks";
import { Bin, Item, Packer } from "@owens3364/3d-bin-packing";

import useSuggestedAddresses from "pages/Components/CartAndCheckout/hooks/useSuggestedAddresses";
import useToast from "core/hooks/useToast";
import { FieldList } from "core/hooks/useForm";
import { useAppState } from "core/contexts/AppContext";
import { ADD_SHIPPING_TO_ORDER, getShippingOptions, REMOVE_SHIPPING_FROM_ORDER } from "core/includes/queries";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { getCountryCodeFromName } from "theme/utils";
import { getCanRenderWebp } from "core/includes/image";

type Props = {
    filteredFields: {
        fields: FieldList,
        billingFields: FieldList,
        contactFields: FieldList,
        readOnlyFields: string[]
    }
}

const useShipping = ({ filteredFields }: Props) => {
    const [, errorToast] = useToast();
    const [appState, setAppState] = useAppState();

    const suggestedAddresses = useSuggestedAddresses();
    const { processCart, getLocalStorageCartUuid } = useCart();

    const [loadShippingOptions] = useLazyQuery(getShippingOptions);
    const [addShippingToOrder] = useMutation(ADD_SHIPPING_TO_ORDER);
    const [removeShippingFromOrder] = useMutation(REMOVE_SHIPPING_FROM_ORDER);

    /**
     * Gets shipping option based on the passed parameters of the product. These dimensions should be set in the CMS.
     * NZPostAPI is used for NZ customers, ShipptAPI is used for all customers.
     *
     * When we get all the appropriate shipping options for an item, we filter out by the cheapest and that require signature
     * add-on. Since an add-one has its own price, we have to add it separately to the cost. If such shipping exists,
     * then we add it to the cart.
     *
     * If no shipping is found, then a user cannot check out :P
     *
     * @param {number} parcelWeight
     * @param {number} parcelWidth
     * @param {number} parcelHeight
     * @param {number} parcelDepth
     *
     * @return {Promise}
     */
    const getShipping = useCallback(async (
        parcelWeight: number,
        parcelWidth: number,
        parcelHeight: number,
        parcelDepth: number,
    ) => {
        let shippingOptions: any = [];
        let suburbOptions: string[] = [];
        let shippingOption: any = null;

        if (!appState.cart) {
            return { shippingOptions, suburbOptions, shippingOption };
        }

        await loadShippingOptions({
            variables: {
                input: JSON.stringify({
                    quote: {
                        "dropoff_address": filteredFields.contactFields.addressOne + ',' + filteredFields.contactFields.addressTwo,
                        "dropoff_postcode": filteredFields.contactFields.postCode,
                        "dropoff_state": filteredFields.contactFields.state,
                        "dropoff_suburb": filteredFields.contactFields.city,
                        "dropoff_country_code": getCountryCodeFromName(filteredFields.contactFields.country),
                        "dutiable_amount": appState.cart?.subtotalAmount ?? 0, // for international orders
                        "return_all_quotes": true,
                        "parcel_attributes": [
                            {
                                "qty": 1,
                                "weight": parcelWeight,
                                "width": Math.round(parcelWidth),
                                "height": Math.round(parcelHeight),
                                "depth": Math.round(parcelDepth),
                            }
                        ]
                    }
                }),
                orderCountryCode: appState.cart.countryCode
            },
        }).then((res: any) => {
            if (res.error?.graphQLErrors.length) {
                throw new Error(res.error.graphQLErrors[0].debugMessage);
            }

            shippingOptions = res.data?.shippingOptions;
            if (shippingOptions?.length) {
                shippingOptions = [...shippingOptions].sort((a: ShippingOption, b: ShippingOption) => (
                    a.priceExcludingGST - b.priceExcludingGST
                ));

                shippingOption = shippingOptions[0];
                const shippingCost = shippingOption.priceExcludingGST;
                // CPSR: Courier Post Signature Required
                const signatureAddon = shippingOption.addons?.filter((addon: ShippingAddons) => addon.addonCode === 'CPSR');

                setAppState((prevState: any) => ({
                    cart: {
                        ...prevState.cart,
                        shippingAmount: shippingCost + (signatureAddon?.[0]?.priceExcludingGST || 0)
                    }
                }));

                addShippingToOrder({
                    variables: {
                        orderUuid: appState.cart.orderUuid,
                        name: (shippingOption.niceTitle + " (" + shippingOption.serviceCode + ")") ?? "",
                        amount: shippingOption.priceExcludingGST ?? 0,
                        canRenderWebp: getCanRenderWebp()
                    }
                }).then(res => {
                    const cart = res.data.addShippingToOrder;
                    processCart(cart);
                    return cart;
                })
            } else {
                errorToast('Please check your address.');

                // Get the suggested city (suburb) using "Address line 1" & "Postcode" through NZ Post API.
                suggestedAddresses.getSuggestedDetailedAddresses({
                    address: filteredFields.contactFields.addressOne,
                    postCode: filteredFields.contactFields.postCode
                }).then(({ address }) => {
                    if (filteredFields.contactFields.city !== address.city) {
                        suburbOptions = [address.city]
                    }
                });
            }
        }).catch(() => {
            errorToast('Error has occurred. Please contact us.');
        });

        return { shippingOptions, shippingOption, suburbOptions };
    }, [appState.cart, loadShippingOptions, filteredFields.contactFields, setAppState, addShippingToOrder, processCart, errorToast, suggestedAddresses]);

    /**
     * Goes over the cart's products and trying to pack it into the box that was passed. If one of the dimensions is not
     * set then we will show the error message to a customer and they would not be able to check out.
     *
     * @param {Packer} packer
     * @return {boolean}
     */
    const fillBox = useCallback((packer: Packer) => {
        let hasMissingAttributes = false;
        appState.cart?.products?.forEach((cp: CartProduct) => {
            // The type 'event' doesn't have any weight or length information.
            if (cp.type !== 'event') {
                if (!cp.width || !cp.depth || !cp.height) {
                    hasMissingAttributes = true;
                }

                for (let i = 0; i < cp.quantity; i++) {
                    packer.addItem(new Item(`Item ${i + 1}`, cp.width, cp.height, cp.depth));
                }
            }
        });
        return hasMissingAttributes;
    }, [appState.cart?.products]);

    /**
     * Goes over all available package boxes and tries to pack the products into them. Based on this we find the most
     * appropriate package for the user to use. This will be used to identify the price of shipping later.
     *
     * @return {Promise}
     */
    const packageProducts = useCallback(async() => {
        let shippingOptions: any = [];

        if (!appState.cart) return shippingOptions;

        const parcelWeightKgs = appState.cart.products.reduce((a: number, b: CartProduct) => {
            let bTotalWeight = (b.weight || 0) * b.quantity;
            return a + bTotalWeight;
        }, 0);

        let boxIndex = 0;

        for (const nzPostBox of NZ_POST_BOXES) {
            const box = new Bin('box', nzPostBox.widthCms, nzPostBox.heightCms, nzPostBox.depthCms);
            const packer = new Packer();
            packer.addBin(box);

            let hasMissingAttributes = fillBox(packer);

            if (hasMissingAttributes) {
                errorToast('Error has occurred. Please contact us.');
                return;
            }

            packer.pack();

            if (packer.unfitItems.length && boxIndex !== NZ_POST_BOXES.length - 1) {
                boxIndex++;
            } else {
                break;
            }
        }

        const deliveryBox = NZ_POST_BOXES[boxIndex];
        await getShipping(
            parcelWeightKgs,
            deliveryBox.widthCms,
            deliveryBox.heightCms,
            deliveryBox.depthCms,
        )
            .then((res) => shippingOptions = res)
            .catch(() => {
                errorToast('Error has occurred. Please contact us.');
            });

        return shippingOptions;
    }, [appState.cart, errorToast, fillBox, getShipping]);

    /**
     * Removes the shipping from the order. Mainly for when a customer navigates away. The updated cart is returned.
     *
     * @return {Cart} cart
     */
    const removeShipping = useCallback(() => {
        removeShippingFromOrder({
            variables: {
                orderUuid: appState.cart?.orderUuid || getLocalStorageCartUuid(),
                canRenderWebp: getCanRenderWebp()
            }
        }).then(res => {
            const cart = res.data.removeShippingFromOrder;
            processCart(cart);
            return cart;
        }).catch(() => {
            errorToast('Error has occurred. Please contact us.');
        });
    }, [appState.cart?.orderUuid, errorToast, getLocalStorageCartUuid, processCart, removeShippingFromOrder]);

    return {
        packageProducts,
        removeShipping,
    }
}

export const NZ_POST_BOXES = [
    {
        widthCms: 23.5,
        heightCms: 7,
        depthCms: 16.5,
    },
    {
        widthCms: 25,
        heightCms: 17,
        depthCms: 18.5,
    },{
        widthCms: 35,
        heightCms: 20,
        depthCms: 26.5,
    },{
        widthCms: 31.8,
        heightCms: 50.7,
        depthCms: 21.6,
    },{
        widthCms: 41.8,
        heightCms: 54,
        depthCms: 28.6,
    },
];

export default useShipping;